import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  BeWizrCourse,
  BeWizrEmbedItem,
  BeWizrLearningTrack,
  BeWizrLibraryItem,
  BeWizrLibraryItemType,
  BeWizrLinkItem,
  BeWizrMediaItem,
  BeWizrMeeting,
} from '~/models'
import { memo } from '~/ui/component'
import { HBox, Label, ListBar, SVG } from '~/ui/components'
import { layout } from '~/ui/styling'
import { register } from './registry'

export interface Props {
  item: BeWizrLibraryItem
}

const BeWizrLibraryItemSearchResult = memo('BeWizrLibraryItemSearchResult', (props: Props) => {

  const {item} = props

  const [t] = useTranslation('bewizr-library')

  const type = React.useMemo((): BeWizrLibraryItemType => {
    return BeWizrLibraryItemType.typeForModel(item)
  }, [item])

  const icon = React.useMemo(
    () => BeWizrLibraryItemType.iconForType(type),
    [type],
  )

  const image = React.useMemo(() => {
    const isMiscItem = item instanceof BeWizrEmbedItem || item instanceof BeWizrLinkItem || item instanceof BeWizrMediaItem
    if (item.links.image != null) {
      return item.links.image
    } else if (isMiscItem && item.type === 'embeditem') {
      return item.embedJson.thumbnailUrl
    } else if (isMiscItem && item?.type === 'linkitem') {
      return item.openGraphJson?.image
    } else if (isMiscItem && item?.type === 'mediaitem') {
      return item.images[0]
    }
  }, [item])

  const href = React.useMemo(() => {
    if (item instanceof BeWizrCourse) {
      return `//bewizr-course/${item.slug}`
    } else if (item instanceof BeWizrLearningTrack) {
      return `//bewizr-learning-track/${item.id}`
    } else if (item instanceof BeWizrMeeting) {
      const occurrenceID = item.occurrences.length === 1 ? item.occurrences[0].id : null
      return `//bewizr-meeting/${item.id}${occurrenceID != null ? `/occurrences/${occurrenceID}` : ''}`
    } else if (item instanceof BeWizrLinkItem) {
      return `//bewizr-misc-item/link/${item.id}`
    } else if (item instanceof BeWizrEmbedItem) {
      return `//bewizr-misc-item/embed/${item.id}`
    } else if (item instanceof BeWizrMediaItem) {
      return `//bewizr-misc-item/media/${item.id}`
    }
  }, [item])

  //------
  // Rendering

  function render() {
    return (
      <ListBar
        image={image == null ? undefined : {type: 'remote', url: image}}
        caption={item.title}
        detail={renderSubtitle()}
        accessory={<SVG name='chevron-right' size={layout.icon.m} dimmer/>}
        href={href}
      />
    )
  }

  function renderSubtitle() {
    return (
      <HBox gap={layout.padding.inline.s}>
        <SVG name={icon} size={layout.icon.s} dim/>
        <Label small dim>
          {item.shortDescription != null ? item.shortDescription : t(`item-types.${type}`)}
        </Label>
      </HBox>
    )
  }

  return render()

})

register('bewizr-library-items', BeWizrLibraryItemSearchResult)